---
title: Feature profiles
summary: What each deployment profile enables, and how the enabled-features delta works.
visibility: authenticated
audience: author-admin
section: Reference
order: 75
last_verified: 2026-07-27
owner: docs
---

# Feature profiles

Not every deployment has every feature. A **feature** is a capability toggled on or off
for a deployment (for example, *voice*, *whatsapp*, or *commerce*). A **profile** is a
named preset bundle of features chosen when the deployment is set up. What you see in the
app — which pages, modes, and options exist — depends on the active profile and any
adjustments on top of it.

## The profiles

| Profile | What it enables (in brief) |
|---|---|
| `full` | Everything: voice, avatars, inbox, widget, WhatsApp, marketing, commerce, CRM, RBAC, and the advanced persona runtime (wizard, tools, workflows). |
| `smb` | Same as `full` — the complete small-business feature set. |
| `knowledge-ops` | RAG-heavy deployments with channels and workflows: voice, avatars, inbox, widget, WhatsApp, RBAC, and the advanced persona runtime (wizard, tools, workflows) — no marketing, commerce, or CRM. |
| `edge-local` | Core and voice only — for constrained/offline-leaning deployments. |
| `minimal` | Core only. |

"Core" is always on and can't be turned off.

## Adjusting a profile

A deployment can fine-tune its profile with an **enabled-features delta** — a list applied
on top of the profile:

- A bare name **adds** a feature (for example, `inbox`).
- A name with a leading minus **removes** one (for example, `-avatars`).

So a profile plus `inbox,widget,-avatars` means "this profile, but also turn on inbox and
widget, and turn off avatars."

## Dependencies

Some features depend on others — for example, `whatsapp` needs `inbox`, and `commerce`
needs `whatsapp` (and therefore `inbox`). Dependencies are **validated, not added for you**:
if you enable a feature without its dependencies, the deployment refuses to start rather
than guessing. Include the whole chain explicitly.

## Checking what's enabled

The app reads the live feature set from a public manifest at `GET /api/features`, which
reports the active profile and the resolved list of enabled features. This is the source of
truth these docs use to show or hide feature-specific pages.

## See also

- [Glossary](/help/reference/glossary) — *feature*, *profile*.
